import { useEffect, useId, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DateTimeField,
  MutationError,
  latestError,
  ProblemError,
  hasGrant,
  usePrincipal,
} from "@csa/web-foundation";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Table,
  TableCard,
} from "../../ui";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import {
  CONFIG_ACTIVATE_ACTION,
  type Approval,
  type ConfigStatus,
  type ConfigVersion,
  type GovernanceLevel,
} from "../../api/config-contracts";
import {
  useApproveApproval,
  useAwaitingMe,
  useConfigDashboard,
  useMyApprovals,
  useNamespace,
  useNamespaceAt,
  useProposeConfig,
  useActivateConfig,
  useRejectConfig,
  useRejectApproval,
  useRequestApproval,
} from "./use-config";

// AL3 — Score-Registry config governance. The governed change path made operable:
// propose → (NORMAL_ADMIN) activate directly, or (FOUR_EYES) request a config.activate
// approval → a SECOND person grants from their inbox → the proposer activates carrying
// the approvalId. Governance is server-enforced and surfaced through the GA1 Approval
// API + the foundation client — never a single-actor publish, never a parallel flow.
// The UI branches on the API-provided governanceLevel, never a hard-coded list. The
// guardrails are sacred and server-side: the browser proposes a value and surfaces the
// server's VALIDATION_FAILED — it never replicates or bypasses a guardrail.

// Human-readable presentation for the registry namespaces. The server returns the
// stable CODE (PRIORITY, RENEWAL_CADENCE, …) as the identity; here we pair each known
// code with a Türkçe title + a one-line "what it does" so an operator does not have
// to decode the enum. PRESENTATION ONLY — the code stays the source of truth (shown
// as a small mono subtitle), and an unknown/future code falls back to the raw code
// with no invented description.
const NS_META: Record<string, { title: MessageKey; desc: MessageKey }> = {
  PRIORITY: { title: "cfg.ns.PRIORITY.title", desc: "cfg.ns.PRIORITY.desc" },
  RENEWAL_CADENCE: { title: "cfg.ns.RENEWAL_CADENCE.title", desc: "cfg.ns.RENEWAL_CADENCE.desc" },
  GAP_RULES: { title: "cfg.ns.GAP_RULES.title", desc: "cfg.ns.GAP_RULES.desc" },
  SUPPRESSION_GATES: {
    title: "cfg.ns.SUPPRESSION_GATES.title",
    desc: "cfg.ns.SUPPRESSION_GATES.desc",
  },
  LAWFUL_BASIS_MAP: {
    title: "cfg.ns.LAWFUL_BASIS_MAP.title",
    desc: "cfg.ns.LAWFUL_BASIS_MAP.desc",
  },
  RECONCILIATION: { title: "cfg.ns.RECONCILIATION.title", desc: "cfg.ns.RECONCILIATION.desc" },
  CARRIER_QUALITY: { title: "cfg.ns.CARRIER_QUALITY.title", desc: "cfg.ns.CARRIER_QUALITY.desc" },
};

export function ConfigGovernance() {
  const { t } = useT();
  const dashboard = useConfigDashboard();
  const [selected, setSelected] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  // A7 (#293) — a config.activate approval's "İncele" deep-links here with
  // ?ns=<namespace>; open that namespace's detail so the approver SEES what they are
  // being asked to activate before they grant. Reacts to the query param, so an
  // in-app navigate from the inbox re-selects without a reload.
  useEffect(() => {
    const ns = searchParams.get("ns");
    if (ns) setSelected(ns);
  }, [searchParams]);

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("cfg.heading")}</h1>
          <p className="surf-sub muted">{t("cfg.subheading")}</p>
        </div>
      </div>

      <TableCard
        title={t("cfg.registryTitle")}
        footer={
          <span className="muted surf-foot-note">
            <Icon name="lock" size={13} strokeWidth={1.9} /> {t("cfg.governNote")}
          </span>
        }
      >
        {dashboard.isLoading && <LoadingState label={t("cfg.loading")} rows={3} />}
        {dashboard.error instanceof ProblemError && (
          <ErrorState title={t("cfg.loading")} description={dashboard.error.problem.detail} />
        )}
        {dashboard.data && (
          <Table>
            <thead>
              <tr>
                <th>{t("cfg.namespace")}</th>
                <th>{t("cfg.governance")}</th>
                <th>{t("cfg.active")}</th>
                <th className="cat-td-right" />
              </tr>
            </thead>
            <tbody>
              {dashboard.data.namespaces.map((ns) => (
                <tr key={ns.namespace} data-testid="cfg-ns-row">
                  <td>
                    {NS_META[ns.namespace] ? (
                      <div className="cfg-ns-cell">
                        <span className="cat-strong">{t(NS_META[ns.namespace]!.title)}</span>
                        <code className="cfg-ns-code">{ns.namespace}</code>
                        <span className="cfg-ns-desc muted">{t(NS_META[ns.namespace]!.desc)}</span>
                      </div>
                    ) : (
                      <span className="cat-strong">{ns.namespace}</span>
                    )}
                  </td>
                  <td>
                    <GovernanceBadge level={ns.governanceLevel} />
                  </td>
                  <td>
                    {ns.active ? (
                      <span className="cfg-activever">v{ns.active.version}</span>
                    ) : (
                      <span className="muted">{t("cfg.noActive")}</span>
                    )}
                  </td>
                  <td className="cat-td-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      type="button"
                      onClick={() => setSelected(ns.namespace)}
                    >
                      {t("cfg.manage")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      {selected && <NamespaceDetailView namespace={selected} />}

      <ApproverInbox />
    </section>
  );
}

function GovernanceBadge({ level }: { level: GovernanceLevel }) {
  const { t } = useT();
  const isFourEyes = level === "FOUR_EYES";
  return (
    <span
      className={`k-govbadge ${isFourEyes ? "k-govbadge--foureyes" : "k-govbadge--normal"}`}
      data-testid="gov-badge"
      data-level={level}
    >
      <Icon name={isFourEyes ? "vault" : "lock"} size={12} strokeWidth={1.9} />
      {isFourEyes ? t("cfg.badge.fourEyes") : t("cfg.badge.normal")}
    </span>
  );
}

// Config lifecycle status → a calm kit badge tone. ACTIVE reads success; awaiting-approval
// is amber (a governance wait, not an alarm); rejected is the quiet danger pill; draft /
// superseded are neutral. Copy stays the i18n label; the testid the surface tests hook stays.
const STATUS_BADGE: Record<ConfigStatus, string> = {
  DRAFT: "k-badge--standard",
  PENDING_APPROVAL: "k-badge--warning",
  ACTIVE: "k-badge--success",
  SUPERSEDED: "k-badge--standard",
  REJECTED: "k-badge--priority",
};

function ConfigStatusBadge({ status }: { status: ConfigStatus }) {
  const { t } = useT();
  return (
    <span className={`k-badge ${STATUS_BADGE[status]}`} data-testid="cfg-status">
      {t(`cfg.status.${status}` as `cfg.status.${ConfigStatus}`)}
    </span>
  );
}

function NamespaceDetailView({ namespace }: { namespace: string }) {
  const { t } = useT();
  const { me } = usePrincipal();
  const detail = useNamespace(namespace);
  const canPropose = hasGrant(me, "config:propose", "all");
  const canActivate = hasGrant(me, "config:activate", "all");

  if (detail.isLoading) return <LoadingState label={t("cfg.loading")} rows={3} />;
  if (detail.error instanceof ProblemError)
    return <ErrorState title={t("cfg.loading")} description={detail.error.problem.detail} />;
  if (!detail.data) return null;
  const d = detail.data;

  return (
    <div className="cfg-detail surf-stack" data-testid="cfg-detail">
      <Card>
        <div className="surf-cardhead">
          <div className="cfg-detail-head">
            <span className="cfg-detail-eyebrow muted">{t("cfg.registryTitle")}</span>
            <h2 className="cfg-detail-name">
              {NS_META[namespace] ? t(NS_META[namespace]!.title) : namespace}
            </h2>
            {NS_META[namespace] && (
              <>
                <code className="cfg-ns-code">{namespace}</code>
                <p className="cfg-detail-desc muted">{t(NS_META[namespace]!.desc)}</p>
              </>
            )}
          </div>
          <GovernanceBadge level={d.governanceLevel} />
        </div>

        <div className="cfg-active" data-testid="cfg-active">
          <div className="cfg-active-head">
            <span className="muted">{t("cfg.active")}</span>
            {d.active ? (
              <span className="cfg-activever">v{d.active.version}</span>
            ) : (
              <span className="muted">{t("cfg.noActive")}</span>
            )}
          </div>
          {d.active && (
            <pre className="cfg-payload">{JSON.stringify(d.active.payload, null, 2)}</pre>
          )}
        </div>
      </Card>

      <ProposeForm namespace={namespace} seed={d.active?.payload ?? {}} canPropose={canPropose} />

      <TimeTravel namespace={namespace} />

      <TableCard
        title={t("cfg.history")}
        footer={<span className="muted surf-foot-note">{t("cfg.appendOnlyNote")}</span>}
      >
        <ul className="cfg-history">
          {d.history.map((v) => (
            <li
              key={v.id}
              className="cfg-history-row"
              data-testid="cfg-version"
              data-version={v.version}
              data-status={v.status}
            >
              <span className="cfg-history-meta">
                <strong className="cat-strong">v{v.version}</strong>
                <ConfigStatusBadge status={v.status} />
                <span className="muted">{v.changeReason ?? "—"}</span>
              </span>
              {(v.status === "DRAFT" || v.status === "PENDING_APPROVAL") && (
                <GovernActions
                  namespace={namespace}
                  version={v}
                  governanceLevel={d.governanceLevel}
                  canActivate={canActivate}
                />
              )}
            </li>
          ))}
        </ul>
      </TableCard>
    </div>
  );
}

function ProposeForm({
  namespace,
  seed,
  canPropose,
}: {
  namespace: string;
  seed: Record<string, unknown>;
  canPropose: boolean;
}) {
  const { t } = useT();
  const propose = useProposeConfig(namespace);
  const [text, setText] = useState(() => JSON.stringify(seed, null, 2));
  const [changeReason, setChangeReason] = useState("");

  // CLIENT-SIDE only checks the payload PARSES as a JSON object (a format check, not a
  // guardrail — guardrails are sacred and server-side). The server validates the value.
  let parsed: Record<string, unknown> | null = null;
  let jsonError = false;
  try {
    const p: unknown = JSON.parse(text);
    if (p && typeof p === "object" && !Array.isArray(p)) parsed = p as Record<string, unknown>;
    else jsonError = true;
  } catch {
    jsonError = text.trim() !== "";
  }

  const canSubmit = canPropose && parsed !== null && !propose.isPending;
  const payloadId = useId();

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cfg.propose.heading")}</span>
        <span className="cat-append-pill">
          <Icon name="lock" size={12} strokeWidth={1.9} />
          {t("cfg.appendOnlyTag")}
        </span>
      </div>
      {!canPropose && <p className="muted surf-note">{t("cfg.propose.readOnly")}</p>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit)
            propose.mutate({ payload: parsed!, changeReason: changeReason.trim() || undefined });
        }}
      >
        <fieldset disabled={!canPropose}>
          <div className="k-field">
            <label className="k-field__label" htmlFor={payloadId}>
              {t("cfg.propose.payload")}
            </label>
            <textarea
              id={payloadId}
              className={`k-textarea${jsonError ? " k-input--error" : ""}`}
              rows={8}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            {jsonError ? (
              <span className="k-field__error" data-testid="cfg-json-error">
                <Icon name="alert-circle" size={13} strokeWidth={2.2} />
                {t("cfg.propose.invalidJson")}
              </span>
            ) : (
              <span className="k-field__hint" data-testid="help-payload">
                {t("cfg.propose.payloadHint")}
              </span>
            )}
          </div>
          <Input
            label={t("cfg.propose.changeReason")}
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            hint={t("cfg.propose.changeReasonHint")}
          />
        </fieldset>

        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={propose.isPending}>
            {t("cfg.propose.submit")}
          </Button>
          {propose.isSuccess && (
            <span role="status" className="form-ok">
              {t("cfg.propose.success")}
            </span>
          )}
          {propose.error instanceof ProblemError && (
            <span role="alert" className="form-error">
              {propose.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function GovernActions({
  namespace,
  version,
  governanceLevel,
  canActivate,
}: {
  namespace: string;
  version: ConfigVersion;
  governanceLevel: GovernanceLevel;
  canActivate: boolean;
}) {
  const { t } = useT();
  const activate = useActivateConfig(namespace);
  const reject = useRejectConfig(namespace);
  const requestApproval = useRequestApproval();
  const mine = useMyApprovals();

  const isFourEyes = governanceLevel === "FOUR_EYES";
  // The proposer's own config.activate approval for THIS version (latest first).
  const approval = (mine.data ?? [])
    .filter((a) => a.action === CONFIG_ACTIVATE_ACTION && a.resourceId === version.id)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];
  const approved = approval?.status === "APPROVED";

  // FOUR_EYES: activate is enabled ONLY once a SEPARATE approval is APPROVED (no
  // unilateral activation). NORMAL_ADMIN: a DRAFT activates directly. There is no
  // "approve" control here — the proposer never approves their own proposal; that
  // lives only in the inbox (awaiting-me), which the server excludes their requests from.
  const canDoActivate =
    canActivate && (isFourEyes ? approved : version.status === "DRAFT") && !activate.isPending;

  // GAP-AUDIT-3 D3 kept its intent — a mis-click must not reject a version, and
  // REJECTED is terminal — but the mechanism was globalThis.prompt, dead where
  // prompt is unavailable. Now inline: opening the field is a distinct act from
  // confirming, so a stray click cannot reject. The reason itself stays optional.
  const [rejectingVersion, setRejectingVersion] = useState(false);
  const [versionReason, setVersionReason] = useState("");

  return (
    <span className="cfg-actions" data-testid="cfg-govern">
      {isFourEyes && version.status === "PENDING_APPROVAL" && !approval && (
        <Button
          variant="secondary"
          size="sm"
          type="button"
          data-testid="cfg-request-approval"
          leadingIcon={<Icon name="vault" size={13} strokeWidth={1.9} />}
          disabled={!canActivate || requestApproval.isPending}
          onClick={() =>
            requestApproval.mutate({ action: CONFIG_ACTIVATE_ACTION, resourceId: version.id })
          }
        >
          {t("cfg.requestApproval")}
        </Button>
      )}
      {isFourEyes && approval && !approved && (
        <span className="cfg-awaiting" data-testid="cfg-awaiting">
          <Icon name="lock" size={12} strokeWidth={1.9} />
          {t("cfg.awaitingApprover")}
        </span>
      )}
      <Button
        variant="primary"
        size="sm"
        type="button"
        data-testid="cfg-activate"
        disabled={!canDoActivate}
        onClick={() =>
          activate.mutate({
            version: version.version,
            approvalId: isFourEyes ? approval?.id : undefined,
          })
        }
      >
        {t("cfg.activate")}
      </Button>
      {!rejectingVersion ? (
        <Button
          variant="danger"
          size="sm"
          type="button"
          data-testid="cfg-reject"
          disabled={!canActivate || reject.isPending}
          onClick={() => setRejectingVersion(true)}
        >
          {t("cfg.reject")}
        </Button>
      ) : (
        <span className="cfg-reject-inline">
          <Input
            label={t("cfg.rejectReason")}
            value={versionReason}
            onChange={(e) => setVersionReason(e.target.value)}
            data-testid="cfg-reject-reason"
          />
          <Button
            variant="danger"
            size="sm"
            type="button"
            data-testid="cfg-reject-confirm"
            disabled={reject.isPending}
            onClick={() =>
              reject.mutate({
                version: version.version,
                reason: versionReason.trim() || undefined,
              })
            }
          >
            {t("cfg.reject")}
          </Button>
        </span>
      )}
      {/* Only `activate` reported itself. REJECT is terminal and irreversible, and
          it was the silent one — so an operator could not tell whether they had
          just permanently killed a config version or whether nothing happened.
          `requestApproval` was silent too, which left them staring at an Activate
          button that never enabled with no clue that no approval was ever opened. */}
      <MutationError
        error={activate.error}
        fallback={t("cfg.activateFailed")}
        offline={t("common.offline")}
      />
      <MutationError
        error={reject.error}
        fallback={t("cfg.rejectFailed")}
        offline={t("common.offline")}
      />
      <MutationError
        error={requestApproval.error}
        fallback={t("cfg.requestFailed")}
        offline={t("common.offline")}
      />
    </span>
  );
}

function TimeTravel({ namespace }: { namespace: string }) {
  const { t } = useT();
  const [at, setAt] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const atQuery = useNamespaceAt(namespace, at);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cfg.timeTravel.heading")}</span>
      </div>
      <div className="cfg-timetravel">
        <DateTimeField
          label={t("cfg.timeTravel.at")}
          value={input}
          onChange={setInput}
          todayLabel={t("df.today")}
          nowLabel={t("df.now")}
          data-testid="tt-at"
        />
        <Button
          variant="secondary"
          type="button"
          leadingIcon={<Icon name="calendar" size={14} strokeWidth={1.9} />}
          onClick={() => setAt(input.trim() || null)}
        >
          {t("cfg.timeTravel.go")}
        </Button>
      </div>
      {atQuery.data && (
        <div className="cfg-attime" data-testid="cfg-attime">
          <span className="muted">
            {t("cfg.version")}{" "}
            <strong className="cat-strong">v{atQuery.data.version.version}</strong> ·{" "}
            {atQuery.data.at}
          </span>
          <pre className="cfg-payload">{JSON.stringify(atQuery.data.version.payload, null, 2)}</pre>
        </div>
      )}
      {atQuery.error instanceof ProblemError && (
        <p role="alert" className="form-error">
          {atQuery.error.problem.detail}
        </p>
      )}
    </Card>
  );
}

function ApproverInbox() {
  const { t } = useT();
  const inbox = useAwaitingMe();
  const rows = inbox.data ?? [];

  return (
    <TableCard
      title={t("cfg.inbox.heading")}
      footer={<span className="muted surf-foot-note">{t("cfg.inbox.fourEyesNote")}</span>}
    >
      <div className="cfg-inbox-sub">
        <Icon name="vault" size={15} strokeWidth={1.9} />
        <span>{t("cfg.inbox.subtitle")}</span>
      </div>
      {inbox.isLoading && <LoadingState label={t("cfg.inbox.loading")} rows={2} />}
      {/* A FAILED READ IS NOT AN EMPTY QUEUE. `rows = data ?? []` collapsed the two,
          so a broken GET /v1/approvals/awaiting-me told the approver "no approvals
          awaiting you" — the single most dangerous thing this screen can say to
          someone whose job is to notice pending governance. */}
      {!inbox.isLoading && inbox.error != null && (
        <ErrorState data-testid="cfg-inbox-error" title={t("cfg.inbox.loadFailed")} />
      )}
      {!inbox.isLoading && inbox.error == null && rows.length === 0 && (
        <EmptyState data-testid="cfg-inbox-empty" icon="tasks" title={t("cfg.inbox.none")} />
      )}
      {rows.length > 0 && (
        <ul className="cfg-inbox-list">
          {rows.map((a) => (
            <InboxRow key={a.id} approval={a} />
          ))}
        </ul>
      )}
    </TableCard>
  );
}

function InboxRow({ approval }: { approval: Approval }) {
  const { t } = useT();
  const navigate = useNavigate();
  const approve = useApproveApproval();
  const reject = useRejectApproval();
  const busy = approve.isPending || reject.isPending;
  // A7 (#293) — the server resolved WHERE to review this approval. For an admin
  // target (config.activate → namespace) it is an in-app navigate; when null the
  // row says so honestly rather than leaving a blank that reads as "link failed".
  const review = approval.review ?? null;
  // globalThis.prompt?.() returns undefined where prompt is unavailable, so the
  // handler returned and the button was inert with nothing on screen. Inline.
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  return (
    <li className="cfg-inbox-row" data-testid="cfg-inbox-row" data-action={approval.action}>
      {/* A7 (partial) — the approval row ALREADY carries resourceType (iam.ts:239); it
          was simply never rendered, so an approver saw "config.activate" plus eight hex
          characters and could not tell what kind of thing they were about to approve.
          Showing the type, and the id in full via title, is the honest half.
          A real "open the resource" link is NOT buildable from this row: the id is a
          config_version / recommendation / offer id, and neither the config dashboard
          (which returns only ACTIVE versions) nor the other surfaces accept a deep-link
          param. That needs either a `namespace`/`reviewPath` on the approval read or
          per-surface by-id routes — a BE+FE item, not a client-side map. Tracked
          separately rather than shipped as a link that mostly 404s. */}
      <span className="cfg-inbox-meta">
        <strong className="cat-strong">{approval.action}</strong>
        <span className="muted">
          {t("cfg.inbox.resource")}{" "}
          {approval.resourceType ? (
            <>
              <span data-testid="cfg-inbox-restype">{approval.resourceType}</span>{" "}
            </>
          ) : null}
          <code title={approval.resourceId ?? undefined}>
            {approval.resourceId?.slice(0, 8) ?? "—"}
          </code>{" "}
          · {t("cfg.inbox.requester")} {approval.requestedBy}
        </span>
      </span>
      <span className="cfg-inbox-actions">
        {review && review.app === "admin" ? (
          <Button
            variant="secondary"
            size="sm"
            type="button"
            data-testid="cfg-inbox-review"
            onClick={() => navigate(review.path)}
          >
            {t("cfg.inbox.review")}
          </Button>
        ) : (
          <span className="muted cfg-inbox-noreview" data-testid="cfg-inbox-noreview">
            {t("cfg.inbox.noReview")}
          </span>
        )}
        <Button
          variant="approve"
          size="sm"
          type="button"
          data-testid="cfg-inbox-approve"
          disabled={busy}
          onClick={() => approve.mutate(approval.id)}
        >
          {t("cfg.inbox.approve")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={busy}
          data-testid="cfg-inbox-reject"
          onClick={() => setRejecting((v) => !v)}
        >
          {t("cfg.inbox.reject")}
        </Button>
      </span>
      {rejecting && (
        <div className="cfg-inbox-reject" data-testid="cfg-inbox-reject-form">
          <Input
            label={t("cfg.inbox.rejectReason")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="cfg-inbox-reject-reason"
          />
          <Button
            variant="danger"
            size="sm"
            type="button"
            disabled={busy || !reason.trim()}
            onClick={() => reject.mutate({ approvalId: approval.id, reason: reason.trim() })}
            data-testid="cfg-inbox-reject-confirm"
          >
            {t("cfg.inbox.rejectConfirm")}
          </Button>
        </div>
      )}
      {/* Both grants fired and neither reported its outcome. A four-eyes approval
          refused for self-approval or already-decided left the row unchanged, so
          the approver believed they had approved something they had not. */}
      <MutationError
        error={latestError(approve, reject)}
        fallback={t("cfg.inbox.actionFailed")}
        offline={t("common.offline")}
      />
    </li>
  );
}
